/**
 * api/mcp.js — servidores MCP (Model Context Protocol) do tenant.
 *
 * Endpoints (contrato fixo):
 *   GET    /mcp-servers            → [{ id, name, base_url, is_active, has_auth }]
 *   POST   /mcp-servers            → { name, base_url, auth_header? } → 201
 *   DELETE /mcp-servers/{id}       → 204
 *   POST   /mcp-servers/{id}/test  → { tools_count } (testa tools/list) ou erro
 *
 * Servem como executores de tools (skills marcadas com metadata.is_tool +
 * executor "mcp"). Os segredos (auth_header) nunca voltam no GET — apenas a
 * flag `has_auth`.
 *
 * Convenções:
 *   - queryKey raiz: ['mcp-servers'].
 *   - As mutations invalidam a chave raiz ao ter sucesso.
 *   - Clean: sem lógica de UI.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const mcpKeys = {
  all: () => ['mcp-servers'],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useMcpServers — lista os servidores MCP conectados ao tenant.
 *
 * GET /mcp-servers → [{ id, name, base_url, is_active, has_auth }].
 */
export function useMcpServers() {
  const api = useApi();
  return useQuery({
    queryKey: mcpKeys.all(),
    queryFn:  () => api.get('/mcp-servers'),
    staleTime: 30_000,
  });
}

/**
 * useBuiltinTools — ferramentas NATIVAS (built-in) da plataforma.
 *
 * GET /mcp-servers/builtin → { items: [{ name, provider, label, active }] }.
 * São tools compiladas na plataforma (ex.: graph_query do Graphify) — não são
 * servidores MCP externos. Read-only.
 */
export function useBuiltinTools() {
  const api = useApi();
  return useQuery({
    queryKey: ['mcp-builtin-tools'],
    queryFn:  () => api.get('/mcp-servers/builtin'),
    staleTime: 300_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useCreateMcpServer — registra um novo servidor MCP.
 *
 * POST /mcp-servers body { name, base_url, auth_header? } → 201.
 * Ao ter sucesso invalida a chave raiz ['mcp-servers'].
 *
 * @example
 *   const create = useCreateMcpServer();
 *   create.mutate({ name, base_url, auth_header });
 */
export function useCreateMcpServer() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/mcp-servers', body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: mcpKeys.all() }),
  });
}

/**
 * useDeleteMcpServer — remove um servidor MCP.
 *
 * DELETE /mcp-servers/{id} → 204. Invalida a chave raiz ['mcp-servers'].
 *
 * @example
 *   const remove = useDeleteMcpServer();
 *   remove.mutate({ id });
 */
export function useDeleteMcpServer() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.delete(`/mcp-servers/${id}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: mcpKeys.all() }),
  });
}

/**
 * useTestMcpServer — testa a conectividade de um servidor MCP (tools/list).
 *
 * POST /mcp-servers/{id}/test → { tools_count } ou erro. Invalida a chave raiz
 * para refletir eventual mudança de status (is_active).
 *
 * @example
 *   const test = useTestMcpServer();
 *   test.mutate({ id });
 */
export function useTestMcpServer() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.post(`/mcp-servers/${id}/test`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: mcpKeys.all() }),
  });
}
